import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, Plus, Database, Trash2 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { KBCard, NewKBCard } from '@/components/knowledge-bases/kb-card'
import { knowledgeBaseService } from '@/services/knowledge-base'
import { useOrganizationStore } from '@/stores/organization-store'
import { useDebounce } from '@/hooks/use-debounce'
import type { KnowledgeBase, ChunkingConfig } from '@/schemas/knowledge-base'
import type { AxiosError } from 'axios'

// Filter and sort types
type StatusFilter = 'all' | 'active' | 'inactive'
type SortOption = 'name_asc' | 'name_desc' | 'created_desc' | 'created_asc'

// Dialog state interface
interface DialogState<T> {
  isOpen: boolean
  isProcessing: boolean
  error: string | null
  context: T | null
}

// Initial dialog state helper
function createInitialDialogState<T>(): DialogState<T> {
  return {
    isOpen: false,
    isProcessing: false,
    error: null,
    context: null,
  }
}


// Default chunking config
const DEFAULT_CHUNKING_CONFIG: ChunkingConfig = {
  chunk_size: 1000,
  chunk_overlap: 200,
  embedding_model: 'text-embedding-3-small',
}

// Loading skeleton component
function KBCardSkeleton() {
  return (
    <Card variant="glass" className="p-6 animate-pulse">
      <div className="flex items-start justify-between mb-4">
        <div className="w-14 h-14 rounded-xl bg-gray-700/50" />
        <div className="w-16 h-6 rounded-full bg-gray-700/50" />
      </div>
      <div className="space-y-2 mb-4">
        <div className="h-5 bg-gray-700/50 rounded w-3/4" />
        <div className="h-4 bg-gray-700/50 rounded w-1/2" />
      </div>
      <div className="h-4 bg-gray-700/50 rounded w-full" />
    </Card>
  )
}

// Empty state component
function EmptyState({ hasFilters, onCreateNew, t }: { hasFilters: boolean; onCreateNew: () => void; t: (key: string) => string }) {
  return (
    <div className="col-span-full flex flex-col items-center justify-center py-16">
      <div className="w-20 h-20 rounded-2xl bg-gray-700/30 flex items-center justify-center mb-6">
        <Database className="w-10 h-10 text-gray-500" />
      </div>
      <h3 className="text-xl font-semibold text-gray-200 mb-2">
        {hasFilters ? t('list.noResults') : t('list.emptyState')}
      </h3>
      <p className="text-gray-500 text-center max-w-md mb-6">
        {hasFilters
          ? t('list.noResultsDesc')
          : t('list.emptyStateDesc')}
      </p>
      {!hasFilters && (
        <Button variant="action-emerald" className="gap-2" onClick={onCreateNew}>
          <Plus className="w-5 h-5" />
          {t('list.createFirst')}
        </Button>
      )}
    </div>
  )
}

// No project selected state
function NoProjectState({ t }: { t: (key: string) => string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="w-20 h-20 rounded-2xl bg-amber-500/20 flex items-center justify-center mb-6">
        <Database className="w-10 h-10 text-amber-400" />
      </div>
      <h3 className="text-xl font-semibold text-gray-200 mb-2">
        {t('list.noProjectSelected')}
      </h3>
      <p className="text-gray-500 text-center max-w-md">
        {t('list.noProjectSelectedDesc')}
      </p>
    </div>
  )
}

// Create/Edit KB Form
interface KBFormData {
  name: string
  description: string
  chunk_size: number
  chunk_overlap: number
  embedding_model: string
}

const initialFormData: KBFormData = {
  name: '',
  description: '',
  chunk_size: DEFAULT_CHUNKING_CONFIG.chunk_size,
  chunk_overlap: DEFAULT_CHUNKING_CONFIG.chunk_overlap,
  embedding_model: DEFAULT_CHUNKING_CONFIG.embedding_model,
}

export function KnowledgeBasesPage() {
  const navigate = useNavigate()
  const { t } = useTranslation('knowledge')
  const { currentProject } = useOrganizationStore()

  // Status filter options
  const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: t('list.statusFilter.all') },
    { value: 'active', label: t('list.statusFilter.active') },
    { value: 'inactive', label: t('list.statusFilter.inactive') },
  ]

  // Sort options
  const SORT_OPTIONS: { value: SortOption; label: string }[] = [
    { value: 'name_asc', label: t('list.sort.nameAsc') },
    { value: 'name_desc', label: t('list.sort.nameDesc') },
    { value: 'created_desc', label: t('list.sort.createdDesc') },
    { value: 'created_asc', label: t('list.sort.createdAsc') },
  ]

  // State
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [sortOption, setSortOption] = useState<SortOption>('name_asc')

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [formData, setFormData] = useState<KBFormData>(initialFormData)
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  // Edit dialog state
  const [editDialog, setEditDialog] = useState<DialogState<KnowledgeBase>>(createInitialDialogState())
  const [editFormData, setEditFormData] = useState<KBFormData>(initialFormData)

  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState<DialogState<{ id: string; name: string }>>(
    createInitialDialogState()
  )

  // Debounced search
  const debouncedSearch = useDebounce(searchTerm, 300)

  // Fetch knowledge bases when project or filters change
  useEffect(() => {
    const controller = new AbortController()

    async function fetchKnowledgeBases() {
      if (!currentProject?.id) {
        setKnowledgeBases([])
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      try {
        const isActive =
          statusFilter === 'all' ? undefined : statusFilter === 'active'

        const response = await knowledgeBaseService.listKnowledgeBases(
          currentProject.id,
          {
            search: debouncedSearch || undefined,
            is_active: isActive,
          },
          { signal: controller.signal }
        )
        setKnowledgeBases(response.knowledge_bases)
      } catch (error) {
        if ((error as Error).name !== 'CanceledError') {
          console.error('Failed to fetch knowledge bases:', error)
          setKnowledgeBases([])
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    fetchKnowledgeBases()

    return () => {
      controller.abort()
    }
  }, [currentProject?.id, debouncedSearch, statusFilter])

  // Sorted knowledge bases
  const sortedKnowledgeBases = useMemo(() => {
    return [...knowledgeBases].sort((a, b) => {
      switch (sortOption) {
        case 'name_asc':
          return a.name.localeCompare(b.name)
        case 'name_desc':
          return b.name.localeCompare(a.name)
        case 'created_desc':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        case 'created_asc':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        default:
          return 0
      }
    })
  }, [knowledgeBases, sortOption])

  // Check if any filters are active
  const hasFilters = searchTerm !== '' || statusFilter !== 'all'

  // Handlers
  const handleOpenCreateDialog = () => {
    setFormData(initialFormData)
    setCreateError(null)
    setCreateDialogOpen(true)
  }

  const handleCreateKnowledgeBase = async () => {
    if (!currentProject?.id || !formData.name.trim()) return

    setIsCreating(true)
    setCreateError(null)

    try {
      const response = await knowledgeBaseService.createKnowledgeBase(currentProject.id, {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        chunking_config: {
          chunk_size: formData.chunk_size,
          chunk_overlap: formData.chunk_overlap,
          embedding_model: formData.embedding_model,
        },
      })

      setKnowledgeBases((prev) => [response.knowledge_base, ...prev])
      setCreateDialogOpen(false)
      setFormData(initialFormData)
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>
      setCreateError(axiosError.response?.data?.detail || 'Erro ao criar base de conhecimento')
    } finally {
      setIsCreating(false)
    }
  }

  // Edit handlers
  const handleOpenEditDialog = (kb: KnowledgeBase) => {
    setEditFormData({
      name: kb.name,
      description: kb.description || '',
      chunk_size: kb.chunking_config?.chunk_size || DEFAULT_CHUNKING_CONFIG.chunk_size,
      chunk_overlap: kb.chunking_config?.chunk_overlap || DEFAULT_CHUNKING_CONFIG.chunk_overlap,
      embedding_model: kb.chunking_config?.embedding_model || DEFAULT_CHUNKING_CONFIG.embedding_model,
    })
    setEditDialog({
      isOpen: true,
      isProcessing: false,
      error: null,
      context: kb,
    })
  }

  const handleUpdateKnowledgeBase = async () => {
    if (!currentProject?.id || !editDialog.context || !editFormData.name.trim()) return

    setEditDialog((prev) => ({ ...prev, isProcessing: true, error: null }))

    try {
      const updated = await knowledgeBaseService.updateKnowledgeBase(
        currentProject.id,
        editDialog.context.id,
        {
          name: editFormData.name.trim(),
          description: editFormData.description.trim() || null,
          chunking_config: {
            chunk_size: editFormData.chunk_size,
            chunk_overlap: editFormData.chunk_overlap,
            embedding_model: editFormData.embedding_model,
          },
        }
      )

      setKnowledgeBases((prev) =>
        prev.map((kb) => (kb.id === updated.id ? updated : kb))
      )
      setEditDialog(createInitialDialogState())
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>
      setEditDialog((prev) => ({
        ...prev,
        isProcessing: false,
        error: axiosError.response?.data?.detail || 'Erro ao atualizar base de conhecimento',
      }))
    }
  }

  const handleCloseEditDialog = (open: boolean) => {
    if (!open && !editDialog.isProcessing) {
      setEditDialog(createInitialDialogState())
    }
  }

  // Delete handlers
  const handleOpenDeleteDialog = (kb: KnowledgeBase) => {
    setDeleteDialog({
      isOpen: true,
      isProcessing: false,
      error: null,
      context: { id: kb.id, name: kb.name },
    })
  }

  const handleConfirmDelete = async () => {
    if (!deleteDialog.context || !currentProject) return

    setDeleteDialog((prev) => ({ ...prev, isProcessing: true, error: null }))

    try {
      await knowledgeBaseService.deleteKnowledgeBase(currentProject.id, deleteDialog.context.id)

      setKnowledgeBases((prev) => prev.filter((kb) => kb.id !== deleteDialog.context?.id))
      setDeleteDialog(createInitialDialogState())
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>
      setDeleteDialog((prev) => ({
        ...prev,
        isProcessing: false,
        error: axiosError.response?.data?.detail || 'Erro ao excluir base de conhecimento',
      }))
    }
  }

  const handleCloseDeleteDialog = (open: boolean) => {
    if (!open && !deleteDialog.isProcessing) {
      setDeleteDialog(createInitialDialogState())
    }
  }

  // No project selected
  if (!currentProject) {
    return <NoProjectState t={t} />
  }

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            type="text"
            placeholder={t('list.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white/5 border-gray-700/50 focus:border-cyan-500/50"
          />
        </div>

        {/* Status Filter */}
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as StatusFilter)}
        >
          <SelectTrigger className="w-full sm:w-44 bg-white/5 border-gray-700/50">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="glass border-gray-700/50">
            {STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value} className="focus:bg-white/5">
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Sort */}
        <Select value={sortOption} onValueChange={(value) => setSortOption(value as SortOption)}>
          <SelectTrigger className="w-full sm:w-44 bg-white/5 border-gray-700/50">
            <SelectValue placeholder="Ordenar" />
          </SelectTrigger>
          <SelectContent className="glass border-gray-700/50">
            {SORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value} className="focus:bg-white/5">
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Create Button */}
        <Button variant="action-emerald" className="gap-2 whitespace-nowrap" onClick={handleOpenCreateDialog}>
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">{t('list.newBase')}</span>
        </Button>
      </div>

      {/* Knowledge Bases Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          <>
            {Array.from({ length: 6 }).map((_, index) => (
              <KBCardSkeleton key={index} />
            ))}
          </>
        ) : sortedKnowledgeBases.length === 0 ? (
          <EmptyState hasFilters={hasFilters} onCreateNew={handleOpenCreateDialog} t={t} />
        ) : (
          <>
            {sortedKnowledgeBases.map((kb) => (
              <KBCard
                key={kb.id}
                kb={kb}
                onClick={() => navigate(`/knowledge-base/${kb.id}`)}
                onEdit={() => handleOpenEditDialog(kb)}
                onDelete={() => handleOpenDeleteDialog(kb)}
              />
            ))}
            <NewKBCard onClick={handleOpenCreateDialog} />
          </>
        )}
      </div>

      {/* Create Knowledge Base Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="glass border-gray-700/50 sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('create.title')}</DialogTitle>
            <DialogDescription>
              {t('create.subtitle')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('create.nameRequired')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder={t('create.namePlaceholder')}
                className="bg-white/5 border-gray-700/50"
                disabled={isCreating}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t('create.description')}</Label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder={t('create.descriptionPlaceholder')}
                className="flex w-full rounded-xl border bg-white/5 border-gray-700/50 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 disabled:cursor-not-allowed disabled:opacity-50 min-h-[80px] resize-none"
                disabled={isCreating}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="chunk_size">{t('settings.chunkSize')}</Label>
                <Input
                  id="chunk_size"
                  type="number"
                  value={formData.chunk_size}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, chunk_size: parseInt(e.target.value) || 1000 }))
                  }
                  min={100}
                  max={8000}
                  className="bg-white/5 border-gray-700/50"
                  disabled={isCreating}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="chunk_overlap">{t('settings.chunkOverlap')}</Label>
                <Input
                  id="chunk_overlap"
                  type="number"
                  value={formData.chunk_overlap}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, chunk_overlap: parseInt(e.target.value) || 200 }))
                  }
                  min={0}
                  max={1000}
                  className="bg-white/5 border-gray-700/50"
                  disabled={isCreating}
                />
              </div>
            </div>

            {createError && (
              <p className="text-sm text-red-400 bg-red-500/10 px-3 py-2 rounded-md">{createError}</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateDialogOpen(false)} disabled={isCreating}>
              {t('create.cancel')}
            </Button>
            <Button
              variant="action-emerald"
              onClick={handleCreateKnowledgeBase}
              disabled={!formData.name.trim() || isCreating}
            >
              {isCreating ? t('create.creating') : t('create.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Knowledge Base Dialog */}
      <Dialog open={editDialog.isOpen} onOpenChange={handleCloseEditDialog}>
        <DialogContent className="glass border-gray-700/50 sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('edit.title')}</DialogTitle>
            <DialogDescription>
              {t('edit.subtitle')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">{t('create.nameRequired')}</Label>
              <Input
                id="edit-name"
                value={editFormData.name}
                onChange={(e) => setEditFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder={t('create.namePlaceholder')}
                className="bg-white/5 border-gray-700/50"
                disabled={editDialog.isProcessing}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">{t('create.description')}</Label>
              <textarea
                id="edit-description"
                value={editFormData.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditFormData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder={t('create.descriptionPlaceholder')}
                className="flex w-full rounded-xl border bg-white/5 border-gray-700/50 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 disabled:cursor-not-allowed disabled:opacity-50 min-h-[80px] resize-none"
                disabled={editDialog.isProcessing}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-chunk_size">{t('settings.chunkSize')}</Label>
                <Input
                  id="edit-chunk_size"
                  type="number"
                  value={editFormData.chunk_size}
                  onChange={(e) =>
                    setEditFormData((prev) => ({ ...prev, chunk_size: parseInt(e.target.value) || 1000 }))
                  }
                  min={100}
                  max={8000}
                  className="bg-white/5 border-gray-700/50"
                  disabled={editDialog.isProcessing}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-chunk_overlap">{t('settings.chunkOverlap')}</Label>
                <Input
                  id="edit-chunk_overlap"
                  type="number"
                  value={editFormData.chunk_overlap}
                  onChange={(e) =>
                    setEditFormData((prev) => ({ ...prev, chunk_overlap: parseInt(e.target.value) || 200 }))
                  }
                  min={0}
                  max={1000}
                  className="bg-white/5 border-gray-700/50"
                  disabled={editDialog.isProcessing}
                />
              </div>
            </div>

            {editDialog.error && (
              <p className="text-sm text-red-400 bg-red-500/10 px-3 py-2 rounded-md">{editDialog.error}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => handleCloseEditDialog(false)}
              disabled={editDialog.isProcessing}
            >
              {t('create.cancel')}
            </Button>
            <Button
              variant="action-emerald"
              onClick={handleUpdateKnowledgeBase}
              disabled={!editFormData.name.trim() || editDialog.isProcessing}
            >
              {editDialog.isProcessing ? t('settings.saving') : t('settings.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Knowledge Base Dialog */}
      <ConfirmationDialog
        open={deleteDialog.isOpen}
        onOpenChange={handleCloseDeleteDialog}
        title={t('delete.title')}
        description={
          <>
            {t('delete.message')}{' '}
            <span className="font-semibold text-white">{deleteDialog.context?.name}</span>? {t('delete.messageEnd')}
          </>
        }
        confirmLabel={t('delete.confirm')}
        confirmingLabel={t('delete.confirming')}
        confirmIcon={<Trash2 className="w-4 h-4 mr-2" />}
        confirmVariant="action-red"
        isProcessing={deleteDialog.isProcessing}
        error={deleteDialog.error}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}
